/**
 * Pure cache transformations for optimistic transaction mutations.
 *
 * Three helpers — push / replace / remove — each take the cached envelope
 * shape (`ApiResponse<TransactionPage>` as TanStack Query stores it) and
 * return a new envelope with the requested change applied. NO side effects:
 * no clock, no id minting, no network. The mutation hooks in `api/mutations.ts`
 * own those side effects and pass concrete `Transaction` objects (with the
 * placeholder id + status + createdAt) into these helpers.
 *
 * This separation is deliberate — keeping the cache transforms pure makes
 * them trivially unit-testable (see `optimistic-txn.test.ts`) and the
 * mutation hooks can be tested via the same helpers if integration coverage
 * grows later.
 *
 * Invariants:
 *   - `undefined` cache → returned untouched (no envelope to patch)
 *   - `success === false` envelope → returned untouched (mutating a failed
 *     response would leak optimistic data into the next consumer)
 *   - `pushTransaction` puts the new row at the START of `content`
 *     (matches the prototype + MSW handler sort: newest date first); the
 *     invalidation that fires on settle resyncs against the server-side sort
 *   - `replaceTransaction` matches by id; if the id is not in the cached
 *     page (e.g. user is on page 2 while editing a page-1 row), the cache is
 *     returned untouched — the invalidate-on-settle still pulls fresh data
 *   - `removeTransaction` decrements `totalElements` ONLY when the id was
 *     actually present, so removing a not-cached row doesn't lie about the
 *     total
 */

import type { ApiResponse } from '@/shared/api'
import type { Transaction, TransactionPage } from '../api/contracts'

type TransactionPageCache = ApiResponse<TransactionPage> | undefined

/**
 * Insert `txn` at the top of `cache.data.content`. Increments `totalElements`
 * by 1 — pagination boundaries are not respected on purpose (the server
 * authority resyncs on invalidate); the visual goal is "row appears now".
 */
export function pushTransaction(
  cache: TransactionPageCache,
  txn: Transaction,
): TransactionPageCache {
  if (!cache?.success || !cache.data) return cache
  return {
    ...cache,
    data: {
      ...cache.data,
      content: [txn, ...cache.data.content],
      totalElements: cache.data.totalElements + 1,
    },
  }
}

/**
 * Replace the row with `txn.id` in `cache.data.content`. No-op if the id is
 * not present in the cached page — the optimistic preview only applies where
 * the user can see it; invalidate-on-settle still pulls authoritative data.
 */
export function replaceTransaction(
  cache: TransactionPageCache,
  txn: Transaction,
): TransactionPageCache {
  if (!cache?.success || !cache.data) return cache
  const idx = cache.data.content.findIndex((t) => t.id === txn.id)
  if (idx === -1) return cache
  const nextContent = cache.data.content.slice()
  nextContent[idx] = txn
  return {
    ...cache,
    data: { ...cache.data, content: nextContent },
  }
}

/**
 * Remove the row with the given `id` from `cache.data.content`. Decrements
 * `totalElements` only when the id was actually in the cached page so the
 * count stays honest when the user deletes a row they could not see.
 */
export function removeTransaction(cache: TransactionPageCache, id: string): TransactionPageCache {
  if (!cache?.success || !cache.data) return cache
  const wasPresent = cache.data.content.some((t) => t.id === id)
  if (!wasPresent) return cache
  return {
    ...cache,
    data: {
      ...cache.data,
      content: cache.data.content.filter((t) => t.id !== id),
      totalElements: Math.max(0, cache.data.totalElements - 1),
    },
  }
}
